import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ReactNode } from 'react';
import { WbIcon, WbText } from '@workbench/react';
import { blue, yellow, red, green } from '@mercedes-benz/mui5-theme';
import { TextBoxType } from '../../../utils/interfaces';

const useStyles = makeStyles()((theme: Theme) => ({
    info: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: blue[90],
        padding: theme.spacing(1),
    },
    infoIcon: {
        color: blue[50],
        marginRight: theme.spacing(2),
    },
    infoFontColor: {
        color: blue[50],
    },
    warning: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: yellow[90],
        padding: theme.spacing(1),
    },
    warningIcon: {
        color: yellow[50],
        marginRight: theme.spacing(2),
    },
    warningFontColor: {
        color: yellow[40],
    },
    error: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: red[90],
        padding: theme.spacing(1),
    },
    errorIcon: {
        color: red[50],
        marginRight: theme.spacing(2),
    },
    errorFontColor: {
        color: red[40],
    },
    success: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: green[90],
        padding: theme.spacing(1),
    },
    successIcon: {
        color: green[50],
        marginRight: theme.spacing(2),
    },
    successFontColor: {
        color: green[40],
    },
    leftContent: {
        display: 'flex',
        alignItems: 'center',
    },
}));

interface TextBoxProps {
    text: string | ReactNode;
    text2?: string | ReactNode;
    action?: ReactNode;
    type: TextBoxType;
    textSize?: 'm' | 's' | 'l';
}

const TextBox = (props: TextBoxProps) => {
    const { classes } = useStyles();

    const getMainContainerClass = () => {
        switch (props.type) {
            case 'info':
                return classes.info;
            case 'warning':
                return classes.warning;
            case 'error':
                return classes.error;
            case 'success':
                return classes.success;
            default:
                return classes.info;
        }
    };

    const getFontColorClass = () => {
        switch (props.type) {
            case 'info':
                return classes.infoFontColor;
            case 'warning':
                return classes.warningFontColor;
            case 'error':
                return classes.errorFontColor;
            case 'success':
                return classes.successFontColor;
            default:
                return classes.infoFontColor;
        }
    };

    const getIcon = () => {
        switch (props.type) {
            case 'info':
                return <WbIcon name="bds/info-circle/16" className={classes.infoIcon} />;
            case 'warning':
                return <WbIcon name="bds/exclamation-mark-circle-outline/24" className={classes.warningIcon} />;
            case 'error':
                return <WbIcon name="bds/warning/16" className={classes.errorIcon} />;
            case 'success':
                return <WbIcon name="bds/checkmark-circle/16" className={classes.successIcon} />;
            default:
                return <WbIcon name="bds/info-circle/16" className={classes.infoIcon} />;
        }
    };

    return (
        <div className={getMainContainerClass()}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{ marginRight: 8 }}>{getIcon()}</div>
                <div>
                    <WbText size={props.textSize ?? 'm'} className={getFontColorClass()}>
                        {props.text}
                        {props.text2}
                    </WbText>
                </div>
            </div>
            {props.action && <div style={{ marginLeft: 16 }}>{props.action}</div>}
        </div>
    );
};

export default TextBox;
